import { showConfirm } from '../components/ConfirmDialog';
import { useTranslation, Trans } from 'react-i18next';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useAuth } from '../context/AuthContext';
import {
  apiGetApiKeyBands, apiGetApiKeys, apiCreateApiKey, apiRevokeApiKey,
  apiGetTeam,
  type ApiKeyDTO, type TeamMemberDTO,
} from '../api';
import PageHeader from '../components/PageHeader';

/** The MCP endpoint is served on this same host (nginx proxies /mcp → :3101). */
const mcpEndpoint = () => `${window.location.origin}/mcp`;

const TOOLS_READ = ['list_bands', 'get_band', 'list_shows', 'get_show', 'list_tours', 'list_venues'];
const TOOLS_WRITE = ['create_show', 'update_show', 'add_roadmap_item', 'create_venue', 'create_contact'];

/**
 * ManagerOnboardingPage — Admin tool that designates a band's manager and gives
 * them a scoped MCP access: create a band-scoped API key and show the exact
 * connection guide to hand to the manager. (2026-08-13)
 */
export default function ManagerOnboardingPage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const { t } = useTranslation();
  const { user } = useAuth();
  // Platform admin OR the read-only showcase (demo) account (sample data).
  const isAdmin = user?.global_role === 'superadmin' || !!user?.is_showcase;

  const [bands, setBands] = useState<Array<{ id: string; name: string }>>([]);
  const [bandId, setBandId] = useState('');
  const [team, setTeam] = useState<TeamMemberDTO[]>([]);
  const [keys, setKeys] = useState<ApiKeyDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadBands = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const b = await apiGetApiKeyBands();
      setBands(b);
      setBandId((prev) => prev || (b[0]?.id ?? ''));
    } catch (e: any) {
      setError(e?.message || 'Failed to load bands');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBandData = useCallback(async (id: string) => {
    if (!id) return;
    setError(null);
    try {
      const [teamRows, keyRows] = await Promise.all([
        apiGetTeam({ band: id }),
        apiGetApiKeys(),
      ]);
      setTeam(teamRows);
      setKeys(keyRows.filter((k) => k.band_id === id && !k.revoked));
    } catch (e: any) {
      setError(e?.message || 'Failed to load band data');
    }
  }, []);

  useEffect(() => { void loadBands(); }, [loadBands]);
  useEffect(() => { void loadBandData(bandId); }, [bandId, loadBandData]);

  if (!isAdmin) {
    return (
      <div ref={denseRef} className="page" data-page="manager-onboarding">
        <PageHeader title={t('managerOnboarding.title')} />
        <p className="empty-hint">{t('managerOnboarding.adminRequired')}</p>
      </div>
    );
  }

  const band = bands.find((b) => b.id === bandId);
  const managers = team.filter((t) => (t.tags || []).includes('manager'));

  const create = async () => {
    if (!bandId) { alert(t('managerOnboarding.selectBandFirst')); return; }
    setCreating(true);
    setError(null);
    try {
      const created = await apiCreateApiKey({
        label: `Manager — ${band?.name || bandId}`,
        band_id: bandId,
      });
      setNewKey(created.key);
      setCopied(false);
      await loadBandData(bandId);
    } catch (e: any) {
      setError(e?.message || 'Failed to create access');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k: ApiKeyDTO) => {
    if (!(await showConfirm(t('managerOnboarding.revokeConfirm', { label: k.label })))) return;
    try {
      await apiRevokeApiKey(k.id);
      await loadBandData(bandId);
    } catch (e: any) {
      setError(e?.message || 'Failed to revoke');
    }
  };

  const guideText = (key: string) =>
`You can now manage your Bandz band with your own AI agent.

MCP endpoint:  ${mcpEndpoint()}
Authorization: Bearer ${key}

Available tools:
  Read  → ${TOOLS_READ.join(', ')}
  Write → ${TOOLS_WRITE.join(', ')}

Your key is scoped to your band only. It cannot read or write
other bands, and it never sees the Bandz source code or secrets.
Revoke it at any time by asking your admin.

Test it (curl):
  curl -s -X POST ${mcpEndpoint()} \\
    -H "Authorization: Bearer ${key}" \\
    -H "Content-Type: application/json" \\
    -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'`;

  const copyGuide = () => {
    if (!newKey) return;
    navigator.clipboard?.writeText(guideText(newKey));
    setCopied(true);
  };

  return (
    <div className="page" data-page="manager-onboarding">
      <PageHeader
        title={`🤖 ${t('managerOnboarding.title')}`}
        subtitle={t('managerOnboarding.subtitle')}
      />

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}

      <div className="card" style={{ maxWidth: 720, marginBottom: 20, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>{t('managerOnboarding.pickBand')}</h3>
        <select
          className="form-input"
          style={{ maxWidth: 360 }}
          value={bandId}
          onChange={(e) => { setBandId(e.target.value); setNewKey(null); }}
        >
          {bands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>

        {bandId && (
          <div style={{ marginTop: 14 }}>
            <h4 style={{ margin: '0 0 6px' }}>{t('managerOnboarding.designatedManager')}</h4>
            {loading ? (
              <p className="text-dim">{t('managerOnboarding.loading')}</p>
            ) : managers.length === 0 ? (
              <p className="text-dim" style={{ margin: 0 }}>
                <Trans i18nKey="managerOnboarding.noManager" components={{ strong: <strong />, code: <code /> }}>
                  No manager is designated for <strong>{band?.name}</strong> yet. Tag a person as{' '}
                  <code>manager</code> in <strong>Band Info → Team</strong>, then come back here. You can
                  still create access below without one.
                </Trans>
              </p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {managers.map((m) => (
                  <li key={m.id} style={{ marginBottom: 2 }}>
                    {m.nickname || m.name}
                    {m.email ? <span className="text-dim"> — {m.email}</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="card" style={{ maxWidth: 720, marginBottom: 20, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>{t('managerOnboarding.createHeading')}</h3>
        <p className="text-dim" style={{ fontSize: 'var(--font-sm)', marginTop: 0 }}>
          <Trans i18nKey="managerOnboarding.createDesc" components={{ strong: <strong /> }}>
            Creates a key scoped to <strong>{band?.name || t('managerOnboarding.thisBand')}</strong> only — the manager can
            read &amp; write their own band, never others, and never sees the code.
          </Trans>
        </p>
        <button className="btn btn-primary" onClick={() => void create()} disabled={creating || !bandId}>
          {creating ? t('managerOnboarding.creating') : t('managerOnboarding.createManagerAccess')}
        </button>

        {newKey && (
          <div style={{ marginTop: 14 }}>
            <div style={{ padding: 12, border: '1px solid var(--accent)', borderRadius: 8, background: 'var(--accent-tint)' }}>
              <strong>{t('managerOnboarding.keyCreated')}</strong>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                <code style={{ wordBreak: 'break-all', flex: 1 }}>{newKey}</code>
                <button className="btn btn-secondary btn-small" onClick={() => navigator.clipboard?.writeText(newKey)}>{t('managerOnboarding.copy')}</button>
              </div>
            </div>

            <h4 style={{ margin: '16px 0 6px' }}>{t('managerOnboarding.guide')}</h4>
            <button className="btn btn-small" onClick={copyGuide} disabled={!newKey}>
              {copied ? t('managerOnboarding.copied') : t('managerOnboarding.copyGuide')}
            </button>
            <pre style={{ background: 'var(--bg-elevated)', padding: 12, borderRadius: 6, fontSize: 'var(--font-xs)', overflow: 'auto', marginTop: 8 }}>
{guideText(newKey)}
            </pre>
          </div>
        )}
      </div>

      <div className="card" style={{ maxWidth: 720, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>{t('managerOnboarding.activeManagerAccess')}</h3>
        {keys.length === 0 ? (
          <p className="text-dim">{t('managerOnboarding.noActiveAccess', { band: band?.name || t('managerOnboarding.thisBand') })}</p>
        ) : (
          <div className="table-wrapper">
            <table className="tbl-dense">
              <thead>
                <tr><th>{t('managerOnboarding.label')}</th><th>{t('managerOnboarding.lastUsed')}</th><th></th></tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id}>
                    <td>{k.label}</td>
                    <td className="text-dim">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : t('managerOnboarding.never')}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-danger btn-small" onClick={() => void revoke(k)}>{t('managerOnboarding.revoke')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
